import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, selectIsAuthenticated, logout } from '../store/slices/authSlice';
import { selectIsOpen, selectIsMobile, toggleSidebar } from '../store/slices/sidebarSlice';
import useAlert from "../components/hook/useAlert";
import { RadioTower, User, Users, FileLock, LayoutDashboard, HardDriveDownload } from "lucide-react";
import { useTheme } from '../contexts/ThemeContext';

const SidePanel = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess } = useAlert();
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isOpen = useSelector(selectIsOpen);
  const isMobile = useSelector(selectIsMobile);
  const { theme } = useTheme();

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={24} strokeWidth={2} />, show: true },
    { path: '/client', label: 'Manage Clients', icon: <User size={24} strokeWidth={2} />, show: true },
    { path: '/partner', label: 'Manage Partners', icon: <Users size={24} strokeWidth={2} />, show: true },
    // { path: '/ran', label: 'Manage RANs', icon: <RadioTower size={24} strokeWidth={2} />, show: true },
    // { path: '/licenseGeneration', label: 'License Generation', icon: <FileLock size={24} strokeWidth={2} />, show: true },
    { path: '/finalLicenseGeneration', label: 'License Generation', icon: <FileLock size={24} strokeWidth={2} />, show: true },
    { path: '/downloads', label: 'Tools & Downloads', icon: <HardDriveDownload size={24} strokeWidth={2} />, show: true },
  ];

  const visibleMenuItems = menuItems.filter(item => item.show);

  const handleLogout = () => {
    dispatch(logout());
    showSuccess("Logged out successfully!");
    navigate('/login');
  };

  if (!isAuthenticated) return null;

  return (
    <div
      className={`
        font-sans min-h-screen p-4 fixed left-0 top-0
        transition-all duration-300
        ${!isOpen ? 'w-16' : 'w-64'}
        ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}
        z-50
      `}
      style={{
        backgroundColor: 'var(--sidebar-bg)',
        color: 'var(--sidebar-text)',
        borderRight: `1px solid var(--sidebar-border)`
      }}
    >

      {/* Toggle Button */}
      <button
        onClick={() => dispatch(toggleSidebar())}
        aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-expanded={isOpen}
        title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        className={`
          absolute top-6 rounded-full p-1.5
          border transition-colors
          ${isMobile ? 'right-4' : '-right-3'}
        `}
        style={{
          backgroundColor: 'var(--sidebar-bg)',
          color: 'var(--sidebar-text)',
          borderColor: 'var(--sidebar-border)',
        }}
      >
        <svg
          className={`h-4 w-4 transition-transform ${!isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Logo */}
      <div className="mb-8">
        <div
          className={`
            flex items-center justify-center py-4 border-b
            ${!isOpen ? 'flex-col space-y-1' : 'space-x-3'}
          `}
          style={{ borderColor: 'var(--sidebar-border)' }}
        >
          <div className={`flex items-center justify-center w-full ${!isOpen ? 'flex-col space-y-1' : 'space-x-3'}`}>
            <img src="/secure-document-svgrepo-com.svg" alt="License Icon" className={`${!isOpen ? 'w-6 h-6' : 'w-8 h-8'}`} />
            {isOpen && (
              <h1 className="font-semibold text-md" style={{ color: 'var(--sidebar-text)' }}>
                License Management
              </h1>
            )}
          </div>
        </div>
      </div>

      {/* User Info */}
      {/* {user && (
        <div
          className={`
            mb-6 rounded-lg group transition-all
            ${!isOpen ? 'p-2' : 'p-4'}
          `}
          style={{ backgroundColor: 'var(--sidebar-hover)' }}
        >
          <div className={`flex items-center ${!isOpen ? 'justify-center' : 'space-x-3'}`}>
            
            <div style={{ backgroundColor: 'var(--brand)' }} className="rounded-full p-2">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            
            {isOpen && (
              <div>
                <p className="text-[13px] font-bold truncate" style={{ color: 'var(--sidebar-text)' }}>{user.userName}</p>
                <p className="text-xs truncate" style={{ color: 'var(--sidebar-text-muted)' }}>{user.email}</p>
              </div>
            )}
            
            {!isOpen && (
              <div className="
                absolute left-16 px-3 py-2 rounded text-sm 
                opacity-0 group-hover:opacity-100 transition-opacity 
                pointer-events-none whitespace-nowrap z-50
              "
                style={{ backgroundColor: 'var(--sidebar-bg)', color: 'var(--sidebar-text)' }}
              >
                <div className="font-bold">{user.userName}</div>
                <div className="text-xs" style={{ color: 'var(--sidebar-text-muted)' }}>{user.email}</div>
              </div>
            )}
          </div>
        </div>
      )} */}

      {/* Menu */}
      <nav className="space-y-2" aria-label="Main navigation">
        {visibleMenuItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={isActive ? 'page' : undefined}
              // Collapsed, the icon is the only thing left. Without a name the
              // destination is a guess — for a pointer and for a screen reader
              // alike.
              aria-label={item.label}
              title={!isOpen ? item.label : undefined}
              className={`
                group relative flex items-center rounded-lg transition-all
                ${!isOpen ? 'p-2 justify-center' : 'px-4 py-3 space-x-3'}
              `}
              style={{
                backgroundColor: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
                color: isActive ? 'var(--brand)' : 'var(--sidebar-text)',
                fontWeight: isActive ? 600 : 500,
              }}
            >
              <span className="text-xl">{item.icon}</span>
              {isOpen && <span className="text-sm font-medium">{item.label}</span>}

              {/* Hover label for the collapsed rail. `title` alone waits a
                  second or so before appearing, which is too slow to scan a
                  menu with. */}
              {!isOpen && (
                <span
                  role="tooltip"
                  className="
                    pointer-events-none absolute left-full ml-2 z-50
                    whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium
                    opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100
                    transition-opacity
                  "
                  style={{
                    background: 'var(--card-bg)',
                    color: 'var(--main-text)',
                    border: '1px solid var(--card-border)',
                    boxShadow: 'var(--shadow-dropdown)',
                  }}
                >
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="absolute left-4 right-4 bottom-4">
        <div
          className={`rounded-lg text-center ${!isOpen ? 'p-2' : 'p-3'}`}
          style={{ backgroundColor: 'var(--sidebar-hover)' }}
        >
          <p className={`${!isOpen ? 'text-xs' : 'text-sm'}`} style={{ color: 'var(--sidebar-text)' }}>
            {!isOpen ? 'LSM' : 'License Server Management'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SidePanel;